import { useRef, useState } from "react";

import classNames from "../../utils/classNames";
import {
  DISMANTLE_COMPONENTS,
  DismantleBreakdown,
  DismantleVerdictMeta,
} from "../../utils/dismantleRecommendation";

interface DismantleRecommendationChipProps {
  /** Total 0-100 score (clamped). */
  score: number;
  verdict: DismantleVerdictMeta;
  breakdown: DismantleBreakdown;
  /** Set on demo data so the popover reads honest about the source. */
  isDemo?: boolean;
  className?: string;
}

/**
 * Verdict pill for the Awaiting Inventory "Wilma Intelligence" cell.
 *
 * Hovering / focussing the pill opens a breakdown popover with the nine
 * contributing components (commonality, drivetrain value, sell-through,
 * etc) so an operator can audit *why* a vehicle was recommended for
 * dismantling, review or scrap.
 *
 * Mirrors `PartPriorityChip` for the parts-priority breakdown so the two
 * popovers feel like one consistent system.
 */
export default function DismantleRecommendationChip({
  score,
  verdict,
  breakdown,
  isDemo,
  className,
}: DismantleRecommendationChipProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | undefined>(undefined);

  const handleEnter = () => {
    if (typeof closeTimerRef.current !== "undefined") {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
    setOpen(true);
  };

  const handleLeave = () => {
    if (typeof closeTimerRef.current !== "undefined") {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className={classNames("relative inline-flex items-center", className)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <button
        type="button"
        aria-describedby={open ? "dismantle-breakdown" : undefined}
        className={classNames(
          "inline-flex cursor-help items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
          verdict.chipClass
        )}
        title={`Wilma recommendation ${score}/100 — ${verdict.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={classNames(
            "inline-block h-1.5 w-1.5 rounded-full",
            verdict.segmentClass
          )}
          aria-hidden="true"
        />
        {verdict.shortLabel}
        <span className="tabular-nums opacity-80">{score}</span>
      </button>

      {open && (
        <DismantleBreakdownPopover
          score={score}
          verdict={verdict}
          breakdown={breakdown}
          isDemo={isDemo}
        />
      )}
    </div>
  );
}

interface DismantleBreakdownPopoverProps {
  score: number;
  verdict: DismantleVerdictMeta;
  breakdown: DismantleBreakdown;
  isDemo?: boolean;
}

function DismantleBreakdownPopover({
  score,
  verdict,
  breakdown,
  isDemo,
}: DismantleBreakdownPopoverProps) {
  return (
    <div
      id="dismantle-breakdown"
      role="tooltip"
      // Anchored bottom-right of the chip rather than left so the popover
      // doesn't get clipped when the cell sits at the right edge of the
      // table — the chip is rendered in the card's `aside`, which is
      // right-aligned.
      className="absolute right-0 top-full z-40 mt-2 w-80 cursor-default rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl ring-1 ring-slate-200"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Wilma recommendation
          </p>
          <p className="mt-0.5 flex items-baseline gap-1">
            <span className="text-xl font-bold tabular-nums text-slate-900">
              {score}
            </span>
            <span className="text-xs text-slate-400">/ 100</span>
          </p>
          <p className="mt-1 text-[11px] leading-snug text-slate-500">
            {verdict.description}
          </p>
        </div>
        <span
          className={classNames(
            "inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset",
            verdict.chipClass
          )}
        >
          <span
            className={classNames(
              "inline-block h-1.5 w-1.5 rounded-full",
              verdict.segmentClass
            )}
            aria-hidden="true"
          />
          {verdict.label}
        </span>
      </div>

      <ul className="mt-2 space-y-1.5">
        {DISMANTLE_COMPONENTS.map((comp) => {
          const entry = breakdown[comp.key];
          if (!entry) return null;
          return (
            <BreakdownRow
              key={comp.key}
              label={comp.label}
              description={comp.description}
              score={entry.score}
              detail={entry.detail}
              min={comp.min}
              max={comp.max}
            />
          );
        })}
      </ul>

      <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] leading-snug text-slate-400">
        {isDemo ? (
          <>
            Components are computed from placeholder analytics — real values
            will replace these once the backend publishes them.
          </>
        ) : (
          <>
            Each component contributes between its min and max to the total.
            Penalties (overstock, risk) reduce the score.
          </>
        )}
      </p>
    </div>
  );
}

interface BreakdownRowProps {
  label: string;
  description: string;
  score: number;
  detail: string;
  min: number;
  max: number;
}

function BreakdownRow({
  label,
  description,
  score,
  detail,
  min,
  max,
}: BreakdownRowProps) {
  // Normalise to 0-1 within the component's min/max so the bar fills
  // proportionally for both positive components and negative penalties.
  const range = max - min;
  const fillPct = range === 0 ? 0 : ((score - min) / range) * 100;
  const isNegative = score < 0;
  const isPositive = score > 0;

  const barClass = isNegative
    ? "bg-rose-400"
    : isPositive
      ? "bg-emerald-400"
      : "bg-slate-300";

  return (
    <li className="flex flex-col gap-0.5" title={description}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="truncate font-medium text-slate-700">{label}</span>
        <span
          className={classNames(
            "tabular-nums font-semibold",
            isNegative
              ? "text-rose-600"
              : isPositive
                ? "text-emerald-700"
                : "text-slate-500"
          )}
        >
          {score > 0 ? `+${score}` : score}
          <span className="ml-0.5 text-[10px] font-normal text-slate-400">
            / {max > 0 ? `+${max}` : max}
            {min < 0 ? ` · min ${min}` : ""}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100"
          aria-hidden="true"
        >
          <div
            className={classNames("h-full rounded-full", barClass)}
            style={{
              width: `${Math.max(0, Math.min(100, fillPct))}%`,
            }}
          />
        </div>
        <span className="shrink-0 truncate text-[10px] tabular-nums text-slate-500">
          {detail}
        </span>
      </div>
    </li>
  );
}
